import { call, put } from 'redux-saga/effects';

import { selectIsWebSDK, selectTargetPlatform } from '@pressreader/appcore';
import { type GeneratorResult, selectTyped } from '@pressreader/appstore';
import { error } from '@pressreader/logger';
import type { NavConfigRefocusConfigTopLevelElement, NavConfigRefocusTargetTopLevelElement, ViewName } from '@pressreader/navigation-types';
import { routingManager } from '@pressreader/routing';
import { ExtractReturnType, TargetPlatform } from '@pressreader/types';

import { getParams, viewer } from '../api';
import { type NavigationParams, ReplaceType } from '../types';

import { pushStateToHistoryAction, replaceStateWithReloadHistoryAction } from './actions';

function* goTo(
    params: NavigationParams,
    replaceType: ReplaceType,
): GeneratorResult<{
    isSuccess: boolean;
}> {
    const currentView = viewer?.getActiveView();

    if (currentView && currentView.canLeaveView) {
        try {
            const canLeave: ExtractReturnType<typeof currentView.canLeaveView> = yield call(currentView.canLeaveView, params);

            if (!canLeave) {
                return { isSuccess: false };
            }
        } catch (err) {
            error(err);
        }
    }

    return yield* replaceView(params, replaceType);
}

function* replaceView(params: NavigationParams, replaceType: ReplaceType): GeneratorResult<{ isSuccess: boolean }> {
    let data: ExtractReturnType<typeof routingManager.generateUrl>;
    try {
        data = yield call(routingManager.generateUrl, params);
    } catch (err) {
        error(err);
        return { isSuccess: false };
    }
    let replace = false;

    if (!data) {
        return { isSuccess: false };
    }

    const viewName = params.viewName;

    if (replaceType === ReplaceType.ForceOn) {
        replace = true;
    }

    if (replaceType === ReplaceType.On) {
        replace = viewName === viewer?.getActiveViewName();
    }

    yield call(createState, viewName, params, replace, data.url);
    return { isSuccess: true };
}

function* createState(viewName: ViewName, params: Record<string, unknown>, isReplace: boolean, to: string) {
    const allParams = getParams(viewName, params);
    const state = { ...allParams.stateParams };

    if (isReplace) {
        yield put(replaceStateWithReloadHistoryAction({ to, state }));
    } else {
        yield put(pushStateToHistoryAction({ to, state }));
    }
}

const getRefocusTargetTopLevelElement = (): NavConfigRefocusTargetTopLevelElement => ({
    type: 'topLevelElement',
    timestampMs: Number.POSITIVE_INFINITY,
    refocusTarget: {
        id: 'topLevelElement',
    },
});

const getRefocusConfigurationTopLevelElement = (): NavConfigRefocusConfigTopLevelElement => ({
    id: 'topLevelElement',
    type: 'topLevelElement',
    refocusTarget: {
        id: 'topLevelElement',
    },
    priority: 0,
});

/**
 * What the installing app lends the reader about its views: whether a view name is one the app's React view
 * registry owns. Supplied at installation so the routing saga asks the app, not the component registry, and a
 * spec can answer without registering a view.
 */
export interface ReaderCapabilities {
    isViewRegistered(viewName: string): boolean;
}

/**
 * What the app installed the reader with — `null` until it registered its route finalizer and its query
 * parameter (`registerReaderFinalizer` in the pressreader app). An app that did not, FT and BE among them,
 * has no reader, whatever views it registers: every article open there keeps the navigation it had.
 */
let readerCapabilities: ReaderCapabilities | null = null;

/** Records that the app installed the reader and what it lends it; the app's `registerReaderFinalizer` calls it. */
function markReaderInstalled(capabilities: ReaderCapabilities): void {
    readerCapabilities = capabilities;
}

function __resetReaderInstallationForTests(): void {
    readerCapabilities = null;
}

/** The environment facts the reader-managed decision reads besides the installation. */
export interface ReaderManagedViewEnvironment {
    isWebSDK: boolean;
    targetPlatform: TargetPlatform;
}

/**
 * Whether article opens over a context view go through the reader URL: in an app that installed the
 * reader (`markReaderInstalled`), in the Web app outside the WebSDK, over a view the installing app's
 * registry owns, asked through the predicate it supplied. An app without the reader, legacy-owned
 * views, the WebSDK and the other platforms keep the article navigation they had.
 *
 * The decision itself, for a selector that holds the environment facts already; sagas ask
 * `isReaderManagedContext`, which selects them. Both read the same installation, so a saga and a component
 * never disagree on a view.
 */
function isReaderManagedView(viewName: string | undefined, { isWebSDK, targetPlatform }: ReaderManagedViewEnvironment): boolean {
    if (readerCapabilities === null || viewName === undefined) {
        return false;
    }

    return !isWebSDK && targetPlatform === TargetPlatform.Web && readerCapabilities.isViewRegistered(viewName);
}

/**
 * Whether article opens over a context view go through the reader URL (`isReaderManagedView`), with the
 * environment facts selected from the store. Nothing is selected when no view can be managed: the reader is not
 * installed, or there is no view to read over.
 */
function* isReaderManagedContext(viewName: string | undefined): GeneratorResult<boolean> {
    if (readerCapabilities === null || viewName === undefined) {
        return false;
    }

    const isWebSDK = yield* selectTyped(selectIsWebSDK);
    const targetPlatform = yield* selectTyped(selectTargetPlatform);

    return isReaderManagedView(viewName, { isWebSDK, targetPlatform });
}

export {
    __resetReaderInstallationForTests,
    getRefocusConfigurationTopLevelElement,
    getRefocusTargetTopLevelElement,
    goTo,
    isReaderManagedContext,
    isReaderManagedView,
    markReaderInstalled,
};
